import { ProductService } from "api";
import {
  useQuery,
  useMutation,
  UseMutationOptions,
  UseQueryOptions,
  UseMutationResult,
  QueryKey,
} from "react-query";
import { toast } from "react-toastify";

// Define the type for the parameters
type ListProductParams = [number, string, string];

// Define the type for the request object
interface ListProductRequest {
  queryKey: [number, string, string];
}

/**
 * Default error handler for the admin product hook.
 * @param error - The error object.
 */
const onDefaultError = (error: any) => {
  toast.error(error.message || "An error occurred");
};
/**
 * Hooks for Product pages
 */

/**
 * Custom hook for listing Products.
 * @param page_no - The page number for pagination.
 * @param searchText - The text to search for in Product names.
 * @param status - The status of Products to filter.
 * @param onSuccess - Callback function to execute on successful list operation.
 * @param onError - Callback function to execute on list error.
 * @returns UseQueryResult - React Query hook result for the listProduct query.
 */
const useListProduct = (
  [page_no, searchText, status]: ListProductParams,
  onSuccess: (data: any) => void,
  onError: (error: any) => void = onDefaultError
) => {
  // Define the request object with the correct type
  const request: ListProductRequest = {
    queryKey: [page_no, searchText, status],
  };

  return useQuery(request.queryKey, () => ProductService.listProduct(request), {
    onSuccess,
    keepPreviousData: true,
    onError,
  });
};

/**
 * Custom hook for adding a new Product.
 * @param options - Options for the useMutation hook.
 * @returns UseMutationResult - React Query hook result for the addProduct mutation.
 */
const useAddProduct = (
  options?: UseMutationOptions<unknown, unknown, any, any>
): UseMutationResult<unknown, unknown, any, any> => {
  return useMutation(
    ProductService.addProduct,
    { ...options, onError: onDefaultError } // Add onDefaultError to options
  );
};

/**
 * Custom hook for changing the status of a Product.
 * @param options - Options for the useMutation hook.
 * @returns UseMutationResult - React Query hook result for the updateStatusProduct mutation.
 */
const useProductStatusChange = (
  options?: UseMutationOptions<unknown, unknown, any, any>
): UseMutationResult<unknown, unknown, any, any> => {
  return useMutation(
    ProductService.updateStatusProduct,
    { ...options, onError: onDefaultError } // Add onDefaultError to options
  );
};

/**
 * Custom hook for updating an existing Product.
 * @param options - Options for the useMutation hook.
 * @returns UseMutationResult - React Query hook result for the updateProduct mutation.
 */
const useUpdateProduct = (
  options?: UseMutationOptions<unknown, unknown, any, any>
) => {
  return useMutation(ProductService.updateProduct, options);
};

/**
 * Custom hook for viewing details of a specific Product.
 * @param product_id - The unique identifier of the Product.
 * @param options - Options for the useQuery hook.
 * @returns UseQueryResult - React Query hook result for the viewProduct query.
 */
const useViewProduct = (
  product_id: string,
  options?: UseQueryOptions<ResponseType, Error, ResponseType, [string, string]>
) => {
  return useQuery(
    ["viewProduct", product_id],
    async () => {
      const response = await ProductService.viewProduct({ product_id });
      return response.data;
    },
    options
  );
};

/**
 * Custom hook for deleting a Product.
 * @param options - Options for the useMutation hook.
 * @returns UseMutationResult - React Query hook result for the deleteProduct mutation.
 */
const useProductDelete = (
  options?: UseMutationOptions<unknown, unknown, any, any>
) => {
  return useMutation(ProductService.deleteProduct, options);
};

/**
 * Custom hook for viewing details of a specific product.
 * @param options - Options for the useQuery hook.
 * @returns UseQueryResult - React Query hook result for the getProductList query.
 */
const useGetProductList = (
  options?:
    | Omit<
        UseQueryOptions<ResponseType, Error, ResponseType, QueryKey>,
        "queryKey" | "queryFn"
      >
    | undefined
) => {
  return useQuery<ResponseType, Error>(
    ["product-list"], // Query key
    async () => {
      const response = await ProductService.getProductList({});
      return response.data;
    },
    options
  );
};

/**
 * Custom hook for listing Product List in Sequence.
 * @param options - Options for the useQuery hook.
 * @returns UseQueryResult - React Query hook result for the getProductList query.
 */
const useGetProductListBySequence = (
  options?:
    | Omit<
        UseQueryOptions<ResponseType, Error, ResponseType, QueryKey>,
        "queryKey" | "queryFn"
      >
    | undefined
) => {
  return useQuery<ResponseType, Error>(
    ["get-product-list"], // Query key
    async () => {
      const response = await ProductService.getProductListBySequence({});
      return response.data;
    },
    options
  );
};

/**
 * Custom hook for viewing details of a specific Product.
 * @param product_id - The unique identifier of the Product.
 * @param options - Options for the useQuery hook.
 * @returns UseQueryResult - React Query hook result for the viewProduct query.
 */
const useGetProductDetails = (
  product_id: string,
  options?: UseQueryOptions<ResponseType, Error, ResponseType, [string, string]>
) => {
  return useQuery(
    ["viewProduct", product_id],
    async () => {
      const response = await ProductService.getProductDetails(product_id);
      return response.data;
    },
    options
  );
};

// Exporting the hook directly
const ProductModule = {
  useListProduct,
  useAddProduct,
  useProductStatusChange,
  useUpdateProduct,
  useViewProduct,
  useProductDelete,
  useGetProductList,
  useGetProductListBySequence,
  useGetProductDetails,
};
export default ProductModule;
